import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { useUserContext } from '../../contexts/UserContext';
import type { TrainingPlan, PlanCategory, CategoryProgress } from '../../types/trainingTypes';
import { calculateNextDueDate, calculateCategoryProgress } from '../../utils/trainingUtils';
import { uploadData } from 'aws-amplify/storage';
import '../../css/AffidavitModal.css';

interface AffidavitModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId?: string;
}

/**
 * Training Completion Affidavit modal.
 * Admin signs off on a user's training completion, resetting the compliance cycle.
 *
 * Validates: Requirements 14.1–14.8
 */
const AffidavitModal = ({ isOpen, onClose, userId }: AffidavitModalProps) => {
    const {
        plans,
        topics,
        completions,
        enrollments,
        quizResponses,
        createAffidavit,
    } = useSafetyTrainingContext();

    const { companyOwner, companyUser, allCompanyUsers } = useUserContext();

    // The currently logged-in person (companyUser takes priority since companyOwner
    // is always populated as the company's owner record, not necessarily who's logged in)
    const adminName = companyUser?.name ?? companyOwner?.name ?? 'Admin';
    const adminId = companyUser?.id ?? companyOwner?.id ?? '';
    const companyId = (companyUser?.companyId as string) ?? companyOwner?.companyId ?? '';

    // Selected plan
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    // Selected employee
    const [selectedUserId, setSelectedUserId] = useState<string>(userId ?? '');
    // Signature state
    const [hasSignature, setHasSignature] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const lastPointRef = useRef<{ x: number; y: number } | null>(null);

    // Active plans for dropdown
    const activePlans = useMemo<TrainingPlan[]>(
        () => plans.filter(p => p.status === 'ACTIVE'),
        [plans],
    );

    // Enrolled users for the selected plan
    const enrolledUserIds = useMemo(() => {
        if (!selectedPlanId) return new Set<string>();
        return new Set(
            enrollments
                .filter(e => e.planId === selectedPlanId)
                .map(e => e.userId),
        );
    }, [enrollments, selectedPlanId]);

    // Users available for selection (enrolled in selected plan, excluding current admin)
    const availableUsers = useMemo(() => {
        const base = selectedPlanId
            ? allCompanyUsers.filter(u => enrolledUserIds.has(u.id))
            : allCompanyUsers;
        // Admin cannot sign an affidavit for themselves
        return base.filter(u => u.id !== adminId);
    }, [allCompanyUsers, selectedPlanId, enrolledUserIds, adminId]);

    // Selected plan object
    const selectedPlan = useMemo(
        () => plans.find(p => p.id === selectedPlanId) ?? null,
        [plans, selectedPlanId],
    );

    // Selected user object
    const selectedUser = useMemo(
        () => allCompanyUsers.find(u => u.id === selectedUserId) ?? null,
        [allCompanyUsers, selectedUserId],
    );

    const employeeName = selectedUser?.name ?? '';

    // Cycle length for info banner
    const cycleDuration = selectedPlan?.cycleDuration ?? 0;

    // Today's date string
    const todayStr = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    // Legal affirmation text
    const legalText = useMemo(() => {
        const planName = selectedPlan?.name ?? '[Plan Name]';
        const empName = employeeName || '[Employee Name]';
        return `I, ${adminName}, affirm that after reviewing all Recorded Training Records and verified by the employee's signature that ${empName} has successfully completed training for ${planName} as of ${todayStr}.`;
    }, [adminName, employeeName, selectedPlan, todayStr]);

    // Category breakdown for the selected plan + user
    const categoryBreakdown = useMemo((): { categories: PlanCategory[]; progress: CategoryProgress[] } => {
        if (!selectedPlan || !selectedUserId) return { categories: [], progress: [] };
        let categories: PlanCategory[] = [];
        try {
            categories = JSON.parse(selectedPlan.categories) as PlanCategory[];
        } catch { /* empty */ }
        if (categories.length === 0) return { categories: [], progress: [] };

        const userCompletions = completions.filter(
            c => c.userId === selectedUserId && c.planId === selectedPlanId
        );
        const progress = calculateCategoryProgress(categories, userCompletions, topics);
        return { categories, progress };
    }, [selectedPlan, selectedUserId, selectedPlanId, completions, topics]);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedUserId(userId ?? '');
            setSelectedPlanId(activePlans.length === 1 ? activePlans[0].id : '');
            setHasSignature(false);
            setIsSubmitting(false);
            setError(null);
            clearCanvas();
        }
    }, [isOpen, userId, activePlans]);

    // -----------------------------------------------------------------------
    // Canvas signature pad
    // -----------------------------------------------------------------------

    const getCanvasCoords = useCallback(
        (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } | null => {
            const canvas = canvasRef.current;
            if (!canvas) return null;
            const rect = canvas.getBoundingClientRect();

            if ('touches' in e) {
                const touch = e.touches[0] ?? e.changedTouches[0];
                if (!touch) return null;
                return {
                    x: touch.clientX - rect.left,
                    y: touch.clientY - rect.top,
                };
            }
            return {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };
        },
        [],
    );

    const startDrawing = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            e.preventDefault();
            const coords = getCanvasCoords(e);
            if (!coords) return;
            setIsDrawing(true);
            lastPointRef.current = coords;
            const ctx = canvasRef.current?.getContext('2d');
            if (ctx) {
                ctx.beginPath();
                ctx.moveTo(coords.x, coords.y);
            }
        },
        [getCanvasCoords],
    );

    const draw = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            if (!isDrawing) return;
            e.preventDefault();
            const coords = getCanvasCoords(e);
            if (!coords) return;
            const ctx = canvasRef.current?.getContext('2d');
            if (ctx && lastPointRef.current) {
                ctx.strokeStyle = '#1e293b';
                ctx.lineWidth = 2;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.beginPath();
                ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
                ctx.lineTo(coords.x, coords.y);
                ctx.stroke();
                lastPointRef.current = coords;
                if (!hasSignature) setHasSignature(true);
            }
        },
        [isDrawing, getCanvasCoords, hasSignature],
    );

    const stopDrawing = useCallback(() => {
        setIsDrawing(false);
        lastPointRef.current = null;
    }, []);

    const clearCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
        setHasSignature(false);
    }, []);

    // Resize canvas to match container
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !isOpen) return;
        const resizeCanvas = () => {
            const rect = canvas.getBoundingClientRect();
            canvas.width = rect.width * 2; // 2x for retina
            canvas.height = rect.height * 2;
            const ctx = canvas.getContext('2d');
            if (ctx) ctx.scale(2, 2);
        };
        // Small delay to ensure modal is rendered
        const timer = setTimeout(resizeCanvas, 50);
        return () => clearTimeout(timer);
    }, [isOpen]);

    // -----------------------------------------------------------------------
    // Submit
    // -----------------------------------------------------------------------

    const handleSubmit = useCallback(async () => {
        setError(null);

        if (!selectedPlanId) {
            setError('Please select a training plan.');
            return;
        }
        if (!selectedUserId) {
            setError('Please select an employee.');
            return;
        }
        if (selectedUserId === adminId) {
            setError('You cannot sign an affidavit for yourself. Another authorized person must sign.');
            return;
        }
        if (!hasSignature) {
            setError('Signature required. Please sign the affidavit.');
            return;
        }

        setIsSubmitting(true);

        try {
            // Get signature as blob
            const canvas = canvasRef.current;
            if (!canvas) throw new Error('Canvas not available');

            const blob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob(b => {
                    if (b) resolve(b);
                    else reject(new Error('Failed to export signature'));
                }, 'image/png');
            });

            // Upload signature to S3
            const signatureKey = `signatures/affidavit-${companyId}-${selectedUserId}-${Date.now()}.png`;
            await uploadData({
                path: signatureKey,
                data: blob,
                options: { contentType: 'image/png' },
            });

            // Calculate next due date
            const affidavitDate = new Date();
            const nextDueDate = calculateNextDueDate(affidavitDate, cycleDuration);

            const toLocal = (d: Date) => {
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${y}-${m}-${day}`;
            };

            // Create affidavit record
            await createAffidavit({
                companyId,
                userId: selectedUserId,
                adminId,
                planId: selectedPlanId,
                affidavitDate: toLocal(affidavitDate),
                signaturePath: signatureKey,
                legalText,
                nextDueDate: toLocal(nextDueDate),
            });

            onClose();
        } catch (err) {
            console.error('Failed to submit affidavit:', err);
            setError('Failed to submit affidavit. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }, [
        selectedPlanId, selectedUserId, hasSignature, companyId,
        adminId, cycleDuration, legalText, createAffidavit, onClose,
    ]);

    // -----------------------------------------------------------------------
    // Print
    // -----------------------------------------------------------------------

    const handlePrint = useCallback(() => {
        const canvas = canvasRef.current;
        const signatureDataUrl = canvas?.toDataURL('image/png') ?? '';

        // Build category rows for print
        const categoryRows = categoryBreakdown.progress.map(cp => {
            const isMet = cp.completedMinutes >= cp.targetMinutes;
            return `<tr>
                <td>${cp.categoryName}</td>
                <td>${cp.completedMinutes} min</td>
                <td>${cp.targetMinutes} min</td>
                <td style="color: ${isMet ? '#166534' : '#dc2626'}; font-weight: 600;">${isMet ? '✓ Met' : 'Not Met'}</td>
                <td style="text-align: center;">—</td>
            </tr>`;
        }).join('');

        const categoryTable = categoryBreakdown.categories.length > 0 ? `
            <h3 style="font-size: 15px; margin: 24px 0 8px;">Training Category Summary</h3>
            <table style="width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 20px;">
                <thead>
                    <tr style="background: #f8fafc; border-bottom: 2px solid #e2e8f0;">
                        <th style="text-align: left; padding: 8px;">Category</th>
                        <th style="text-align: left; padding: 8px;">Completed</th>
                        <th style="text-align: left; padding: 8px;">Required</th>
                        <th style="text-align: left; padding: 8px;">Status</th>
                        <th style="text-align: center; padding: 8px;">Initials</th>
                    </tr>
                </thead>
                <tbody>
                    ${categoryRows}
                </tbody>
            </table>
        ` : '';

        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        printWindow.document.write(`
            <html>
            <head>
                <title>Training Completion Affidavit</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 40px; color: #1e293b; }
                    h1 { font-size: 22px; margin-bottom: 24px; }
                    .field { margin-bottom: 12px; }
                    .field-label { font-weight: 600; font-size: 13px; color: #64748b; }
                    .field-value { font-size: 15px; margin-top: 2px; }
                    .legal { padding: 16px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; font-style: italic; margin: 20px 0; line-height: 1.7; }
                    .signature-img { max-width: 300px; border-bottom: 1px solid #1e293b; margin-top: 20px; }
                    .sig-label { font-size: 12px; color: #64748b; margin-top: 4px; }
                    table td, table th { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; }
                </style>
            </head>
            <body>
                <h1>Training Completion Affidavit</h1>
                <div class="field"><div class="field-label">Authorized By</div><div class="field-value">${adminName}</div></div>
                <div class="field"><div class="field-label">Training Plan</div><div class="field-value">${selectedPlan?.name ?? '—'}</div></div>
                <div class="field"><div class="field-label">Employee</div><div class="field-value">${employeeName || '—'}</div></div>
                <div class="field"><div class="field-label">Date</div><div class="field-value">${todayStr}</div></div>
                <div class="legal">${legalText}</div>
                ${categoryTable}
                ${signatureDataUrl ? `<img class="signature-img" src="${signatureDataUrl}" /><div class="sig-label">Authorized Signature</div>` : ''}
            </body>
            </html>
        `);
        printWindow.document.close();
        // Wait for the signature image to load before triggering print
        const img = printWindow.document.querySelector('.signature-img') as HTMLImageElement | null;
        if (img && !img.complete) {
            img.onload = () => printWindow.print();
        } else {
            // Small delay to ensure rendering is complete
            setTimeout(() => printWindow.print(), 100);
        }
    }, [adminName, selectedPlan, employeeName, todayStr, legalText, categoryBreakdown]);

    if (!isOpen) return null;

    // Block self-signing when userId prop matches the logged-in admin
    if (userId && userId === adminId) {
        return (
            <div className="aff-overlay" onClick={onClose}>
                <div className="aff-modal" onClick={e => e.stopPropagation()}>
                    <div className="aff-header">
                        <h2>Training Completion Affidavit</h2>
                        <button className="aff-close-btn" onClick={onClose}>×</button>
                    </div>
                    <div className="aff-body" style={{ textAlign: 'center', padding: '40px 24px' }}>
                        <div style={{ fontSize: 32, marginBottom: 12 }}>🚫</div>
                        <p style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', margin: '0 0 8px' }}>
                            Cannot Self-Certify
                        </p>
                        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
                            You cannot sign a training completion affidavit for yourself.
                            Another authorized person must review and certify your training.
                        </p>
                    </div>
                    <div className="aff-footer">
                        <button className="aff-btn-cancel" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="aff-overlay" onClick={onClose}>
            <div className="aff-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="aff-header">
                    <h2>Training Completion Affidavit</h2>
                    <button className="aff-close-btn" onClick={onClose}>×</button>
                </div>

                {/* Body */}
                <div className="aff-body">
                    {/* Info Banner */}
                    <div className="aff-info-banner">
                        <span className="aff-info-banner-icon">ℹ</span>
                        <span>
                            Once submitted, the date of this affidavit becomes the new Last Completion Date
                            for this user, and the next due date is automatically set{' '}
                            {cycleDuration > 0 ? `${cycleDuration} days` : '[cycle length] days'} later.
                        </span>
                    </div>

                    {/* Error */}
                    {error && <div className="aff-error">{error}</div>}

                    {/* Authorized By */}
                    <div className="aff-field">
                        <label>Authorized By</label>
                        <input type="text" value={adminName} readOnly />
                    </div>

                    {/* Training Plan */}
                    <div className="aff-field">
                        <label>Training Plan</label>
                        <select
                            value={selectedPlanId}
                            onChange={e => setSelectedPlanId(e.target.value)}
                        >
                            <option value="">Select a plan…</option>
                            {activePlans.map(plan => (
                                <option key={plan.id} value={plan.id}>
                                    {plan.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Employee */}
                    <div className="aff-field">
                        <label>Employee</label>
                        {userId ? (
                            <input type="text" value={employeeName} readOnly />
                        ) : (
                            <select
                                value={selectedUserId}
                                onChange={e => setSelectedUserId(e.target.value)}
                            >
                                <option value="">Select an employee…</option>
                                {availableUsers.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.name}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Legal Affirmation */}
                    <div className="aff-legal-text">{legalText}</div>

                    {/* Category Breakdown */}
                    {categoryBreakdown.categories.length > 0 && (
                        <div className="aff-category-breakdown">
                            <div className="aff-category-breakdown-title">Training Category Summary</div>
                            <table className="aff-category-table">
                                <thead>
                                    <tr>
                                        <th>Category</th>
                                        <th>Completed</th>
                                        <th>Required</th>
                                        <th>Status</th>
                                        <th>Initials</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categoryBreakdown.progress.map(cp => {
                                        const isMet = cp.completedMinutes >= cp.targetMinutes;
                                        const planCat = categoryBreakdown.categories.find((c: PlanCategory) => c.id === cp.categoryId);
                                        const quizPassed = planCat?.requiresQuiz
                                            ? quizResponses.find(r =>
                                                r.userId === selectedUserId &&
                                                r.categoryId === cp.categoryId &&
                                                r.passed
                                            )
                                            : null;
                                        return (
                                            <tr key={cp.categoryId}>
                                                <td className="aff-cat-name">{cp.categoryName}</td>
                                                <td>{cp.completedMinutes} min</td>
                                                <td>{cp.targetMinutes} min</td>
                                                <td>
                                                    <span className={`aff-cat-status ${isMet ? 'met' : 'not-met'}`}>
                                                        {isMet ? '✓ Met' : 'Not Met'}
                                                    </span>
                                                </td>
                                                <td className="aff-cat-initials">
                                                    {planCat?.requiresQuiz ? (
                                                        quizPassed?.signaturePath ? (
                                                            <span className="aff-cat-initials-signed">✅ Signed</span>
                                                        ) : (
                                                            <span className="aff-cat-initials-pending">⚠ Quiz not passed</span>
                                                        )
                                                    ) : (
                                                        <span className="aff-cat-initials-na">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Signature Pad */}
                    <div className="aff-signature-section">
                        <div className="aff-signature-label">Authorized Signature</div>
                        <div className={`aff-signature-pad ${hasSignature ? 'has-signature' : ''}`}>
                            <canvas
                                ref={canvasRef}
                                className="aff-signature-canvas"
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                            />
                            {!hasSignature && (
                                <span className="aff-signature-placeholder">Click to sign</span>
                            )}
                        </div>
                        {hasSignature && (
                            <div className="aff-signature-actions">
                                <button className="aff-clear-btn" onClick={clearCanvas}>
                                    Clear signature
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="aff-footer">
                    <button className="aff-btn-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="aff-btn-print" onClick={handlePrint} disabled={!selectedPlanId || !selectedUserId || !hasSignature}>
                        Print Copy
                    </button>
                    <button
                        className="aff-btn-submit"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !selectedPlanId || !selectedUserId || !hasSignature}
                    >
                        {isSubmitting ? 'Submitting…' : 'Submit & Record Completion'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AffidavitModal;
